import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { RootState } from "../store";
import { axiosApi, getResponseError, handleErrorRTKAndAxiosAsync } from "@/helpers";
import { ENDPOINTS_QAS, URL_QAS } from "@/constants/api_endpoints";
import {
	IAddTemplateProps,
	IEditTemplateProps,
	IQasSummareState,
	IResponseAddingTemplate,
	IResponseSummarize,
	IResponseTemplate,
	IResponseTemplatesList,
	ISummarizeProps,
} from "../../types/qas/qasSummareTypes";
import { IResponse, RequestStatus, ResponseStatus } from "../../types/statusTypes";

const initialState: IQasSummareState = {
	templatesList: {
		status: RequestStatus.IDLE,
		data: null,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	template: {
		status: RequestStatus.IDLE,
		data: null,
		error: ResponseStatus.SUCCESS,
		message: "",
		nameChanged: false,
		randomId: Math.random(),
	},
	addingTemplate: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
		id: null,
	},
	editingTemplate: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	deletingTemplate: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	summare: {
		status: RequestStatus.IDLE,
		data: null,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
};

// получение списка шаблонов
export const getTemplatesList = createAsyncThunk("qasSummare/getTemplatesList", async (_, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponseTemplatesList | IResponse> = await axiosApi.get(`${URL_QAS}/${ENDPOINTS_QAS.TEMPLATE_LIST}`);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// получение шаблона
export const getTemplate = createAsyncThunk("qasSummare/getTemplate", async (templateId: string, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponseTemplate> = await axiosApi.get(`${URL_QAS}/${ENDPOINTS_QAS.TEMPLATE_GET}/${templateId}`);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// добавление шаблона
export const addTemplate = createAsyncThunk("qasSummare/addTemplate", async ({ name, options }: IAddTemplateProps, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponseAddingTemplate> = await axiosApi.post(`${URL_QAS}/${ENDPOINTS_QAS.TEMPLATE_ADD}`, {
			name,
			options: JSON.stringify(options),
		});
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// изменение шаблона
export const editTemplate = createAsyncThunk("qasSummare/editTemplate", async ({ templateId, name, options }: IEditTemplateProps, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.post(`${URL_QAS}/${ENDPOINTS_QAS.TEMPLATE_EDIT}/${templateId}`, {
			data: JSON.stringify({ name, options }),
		});
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// удаление шаблона
export const deleteTemplate = createAsyncThunk("qasSummare/deleteTemplate", async (templateId: string, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.delete(`${URL_QAS}/${ENDPOINTS_QAS.TEMPLATE_DELETE}/${templateId}`);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// суммаризация
export const summarize = createAsyncThunk(
	"qasSummare/summarize",
	async ({ text, formData, options, modelGpt, promptTemplateId }: Partial<ISummarizeProps>, { rejectWithValue }) => {
		try {
			const response: AxiosResponse<IResponseSummarize> = await axiosApi.post(
				`${URL_QAS}/${ENDPOINTS_QAS.SUMMARIZE}`,
				{
					text,
					file: formData?.get("file"),
					options: JSON.stringify(options),
					model: modelGpt,
					template: promptTemplateId,
				},
				{
					headers: {
						"Content-Type": "multipart/form-data",
					},
				}
			);
			return response.data;
		} catch (error) {
			const handledError = await handleErrorRTKAndAxiosAsync(error);
			return rejectWithValue(handledError);
		}
	}
);

const qasSummareSlice = createSlice({
	name: "qasSummare",
	initialState,
	reducers: {
		// изменение названия шаблона
		changeNameInTemplate: (state, action: PayloadAction<string>) => {
			if (state.template.data) {
				state.template.data.name = action.payload;
				state.template.nameChanged = true;
			}
		},
		// изменение названия шаблона в списке шаблонов
		changeNameInTemplatesList: (state) => {
			if (state.templatesList.data && state.template.data) {
				state.templatesList.data = state.templatesList.data.map((templateItem) => {
					if (templateItem.id === state.template.data?.id) return { ...templateItem, name: state.template.data.name };
					else return templateItem;
				});
			}
		},
		// изменение раздела правила
		changeRuleChapter: (state, action: PayloadAction<{ oldChapter: string; newChapter: string }>) => {
			if (state.template.data) {
				const arrChangedOptions = Object.entries(state.template.data.options).map((rule) => {
					if (rule[0] === action.payload.oldChapter) return [action.payload.newChapter, rule[1]];
					else return rule;
				});
				const objChangedOptions: Record<string, [string, number]> = Object.fromEntries(arrChangedOptions);
				state.template.data.options = objChangedOptions;
			}
		},
		// изменение описания правила
		changeRuleDescription: (state, action: PayloadAction<{ chapter: string; newDesc: string }>) => {
			if (state.template.data) {
				state.template.data.options[action.payload.chapter] = [action.payload.newDesc, state.template.data.options[action.payload.chapter][1]];
			}
		},
		// добавление правила
		addRule: (state, action: PayloadAction<{ chapter: string; desc: string; id: number }>) => {
			if (state.template.data) {
				state.template.data.options[action.payload.chapter] = [action.payload.desc, action.payload.id];
				state.template.randomId = Math.random(); // меняем id для следующего правила
			}
		},
		// удаление правила
		deleteRule: (state, action: PayloadAction<string>) => {
			if (state.template.data) {
				delete state.template.data.options[action.payload];
			}
		},
		// очистка state
		clearState: (state) => {
			state.templatesList = initialState.templatesList;
			state.template = initialState.template;
			state.addingTemplate = initialState.addingTemplate;
			state.editingTemplate = initialState.editingTemplate;
			state.deletingTemplate = initialState.deletingTemplate;
			state.summare = initialState.summare;
		},
		// очистка списка шаблонов
		clearTemplatesList: (state) => {
			state.templatesList = initialState.templatesList;
		},
		// очистка шаблона
		clearTemplate: (state) => {
			state.template = initialState.template;
		},
		// очистка статуса добавления шаблона
		clearAddingTemplate: (state) => {
			state.addingTemplate = initialState.addingTemplate;
		},
		// очистка статуса изменения шаблона
		clearEditingTemplate: (state) => {
			state.editingTemplate = initialState.editingTemplate;
		},
		// очистка статуса удаления шаблона
		clearDeletingTemplate: (state) => {
			state.deletingTemplate = initialState.deletingTemplate;
		},
		// очистка протокола
		clearSummare: (state) => {
			state.summare = initialState.summare;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getTemplatesList.pending, (state) => {
				state.templatesList.status = RequestStatus.LOADING;
			})
			.addCase(getTemplatesList.fulfilled, (state, action) => {
				if (typeof action.payload === "object" && "templates" in action.payload && Array.isArray(action.payload.templates)) {
					state.templatesList.status = RequestStatus.IDLE;
					state.templatesList.data = action.payload.templates.sort((a, b) => {
						if (a.name > b.name) return 1;
						else if (a.name < b.name) return -1;
						else return 0;
					});
				} else state.templatesList.status = RequestStatus.FAILED;
			})
			.addCase(getTemplatesList.rejected, (state, action: PayloadAction<unknown>) => {
				state.templatesList.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.templatesList.error = error;
				state.templatesList.message = message;
			})
			.addCase(getTemplate.pending, (state) => {
				state.template.status = RequestStatus.LOADING;
			})
			.addCase(getTemplate.fulfilled, (state, action) => {
				if (typeof action.payload === "object" && "data" in action.payload && action.payload.data) {
					state.template.status = RequestStatus.IDLE;
					// state.template.data = action.payload.data;
					const { options, ...data } = action.payload.data;
					// меняем формат правил
					const newOptions: Record<string, [string, number]> = {};
					for (const key in options) {
						newOptions[key] = [options[key], Math.random()];
					}
					state.template.data = { ...data, options: newOptions };
				} else state.template.status = RequestStatus.FAILED;
			})
			.addCase(getTemplate.rejected, (state, action: PayloadAction<unknown>) => {
				state.template.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.template.error = error;
				state.template.message = message;
			})
			.addCase(addTemplate.pending, (state) => {
				state.addingTemplate.status = RequestStatus.LOADING;
			})
			.addCase(addTemplate.fulfilled, (state, action) => {
				if (action.payload) {
					state.addingTemplate.status = RequestStatus.IDLE;
					state.addingTemplate.error = action.payload.error;
					state.addingTemplate.message = action.payload.message;
					if (action.payload.id) state.addingTemplate.id = action.payload.id;
				} else state.addingTemplate.status = RequestStatus.FAILED;
			})
			.addCase(addTemplate.rejected, (state, action: PayloadAction<unknown>) => {
				state.addingTemplate.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.addingTemplate.error = error;
				state.addingTemplate.message = message;
			})
			.addCase(editTemplate.pending, (state) => {
				state.editingTemplate.status = RequestStatus.LOADING;
			})
			.addCase(editTemplate.fulfilled, (state, action) => {
				if (action.payload) {
					state.editingTemplate.status = RequestStatus.IDLE;
					state.editingTemplate.error = action.payload.error;
					state.editingTemplate.message = action.payload.message;
				} else state.editingTemplate.status = RequestStatus.FAILED;
			})
			.addCase(editTemplate.rejected, (state, action: PayloadAction<unknown>) => {
				state.editingTemplate.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.editingTemplate.error = error;
				state.editingTemplate.message = message;
			})
			.addCase(deleteTemplate.pending, (state) => {
				state.deletingTemplate.status = RequestStatus.LOADING;
			})
			.addCase(deleteTemplate.fulfilled, (state, action) => {
				if (action.payload) {
					state.deletingTemplate.status = RequestStatus.IDLE;
					state.deletingTemplate.error = action.payload.error;
					state.deletingTemplate.message = action.payload.message;
				} else state.deletingTemplate.status = RequestStatus.FAILED;
			})
			.addCase(deleteTemplate.rejected, (state, action: PayloadAction<unknown>) => {
				state.deletingTemplate.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.deletingTemplate.error = error;
				state.deletingTemplate.message = message;
			})
			.addCase(summarize.pending, (state) => {
				state.summare.status = RequestStatus.LOADING;
			})
			.addCase(summarize.fulfilled, (state, action) => {
				if (typeof action.payload === "object" && "summary" in action.payload && action.payload.summary) {
					state.summare.status = RequestStatus.IDLE;
					state.summare.data = action.payload.summary;
				} else state.summare.status = RequestStatus.FAILED;
			})
			.addCase(summarize.rejected, (state, action: PayloadAction<unknown>) => {
				state.summare.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.summare.error = error;
				state.summare.message = message;
			});
	},
});

export const {
	changeNameInTemplate,
	changeNameInTemplatesList,
	changeRuleChapter,
	changeRuleDescription,
	addRule,
	deleteRule,
	clearState,
	clearTemplatesList,
	clearTemplate,
	clearAddingTemplate,
	clearEditingTemplate,
	clearDeletingTemplate,
	clearSummare,
} = qasSummareSlice.actions;

export const selectTemplatesList = (state: RootState) => state.qasSummare.templatesList;
export const selectTemplate = (state: RootState) => state.qasSummare.template;
export const selectAddingTemplate = (state: RootState) => state.qasSummare.addingTemplate;
export const selectEditingTemplate = (state: RootState) => state.qasSummare.editingTemplate;
export const selectDeletingTemplate = (state: RootState) => state.qasSummare.deletingTemplate;
export const selectSummare = (state: RootState) => state.qasSummare.summare;

export default qasSummareSlice.reducer;
